import Fuse from 'fuse.js';
import { UploadDirectory, UploadCloud } from '@/utils/uploadHelpers.js';
import {
  foldersize,
  filesize,
  getFormatPrice,
  formatTimestamp,
  currencies,
  hasActiveFilter,
  getFilterItems,
} from '@/elements/utils.js';
import { MOMENT_FORMATS } from '@/utils/cons.js';
import gettext from '@/utils/translationInjector.js';
const { $gettext } = gettext;

export const filesFilter = (file) => ['D', 'S'].includes(file.type);

export const getUploadSize = (item) => {
  switch (item.constructor) {
    case UploadDirectory:
      return getDirectorySize(item.count);
    case UploadCloud:
      return item.size;
    default:
      return getFileSize(item.size);
  }
};

export const getDirectorySize = (count) => (count ? foldersize(count) : $gettext('empty'));

export const getFileSize = (size) => (typeof size === 'number' ? filesize(size) : $gettext('Unknown'));

export const formatDate = (timestamp, format = MOMENT_FORMATS.DATE) => formatTimestamp(timestamp, format);

export const formatPrice = (value, currency) => currencies[currency] + getFormatPrice(value);

export const getUploadPercentage = (upload) => {
  if (!upload) {
    return 0;
  }
  const { size, received, state, count, uploaded } = upload;
  if (!uploaded && !size) {
    return state === 'UPLOAD_FINISHED' ? 100 : 0;
  }
  const getPerc = (current, total) => (total ? Math.min(parseInt((current / total) * 100, 10), 100) : 0);
  const getFileSize = getPerc(received, size);
  const getFileCount = getPerc(uploaded, count);
  return Math.max(getFileSize, getFileCount);
};

export const getAvatarSymbols = ({ name, email }) => {
  return name
    ? name
        .split(' ')
        .slice(0, 2)
        .map((el) => el.charAt(0))
        .join('')
    : email.charAt(0);
};

export const remoteSiteType = (type) => {
  const types = {
    quatrix: $gettext('Quatrix Account'),
    sftp: $gettext('SFTP'),
    s3: $gettext('S3'),
    azureblob: $gettext('Microsoft Azure Blob Storage'),
    azurefiles: $gettext('Microsoft Azure Files Storage'),
    b2: $gettext('Backblaze'),
    'google cloud storage': $gettext('Google Cloud Storage'),
    onedrive: $gettext('Microsoft OneDrive / Sharepoint'),
    box: $gettext('Box'),
    dropbox: $gettext('Dropbox'),
  };
  return types[type];
};

const filterText = ({ text, filters, collection }) => {
  if (!text) {
    return;
  }
  const escapedText = text.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
  const regexp = new RegExp(escapedText, 'i');
  return collection.filter((model) => {
    return filters.text.fields.find((field) => {
      const attr = field.split('.');
      const res = attr[1] ? model[attr[0]]?.[attr[1]] : model[attr[0]];
      return regexp.test(res);
    });
  });
};

export const getListByFilter = ({ field, value, collection }) => {
  return collection.filter((model) => {
    if (typeof field === 'string') {
      if (Array.isArray(model[field]) && model[field].length > 0) {
        return model[field].some((obj) => obj.value === value);
      }
      return model[field] === value;
    } else {
      return field.reduce((m, f) => m[f], model) === value;
    }
  });
};

const DEFAULT_SEARCH_THRESHOLD = 0.35; // https://www.fusejs.io/api/options.html#threshold
const DEFAULT_SEARCH_DISTANCE = 150; // https://www.fusejs.io/api/options.html#distance

export const searchFuzzyMatches = ({ text, collection, fields, params }) => {
  if (text?.length === 0 || collection?.length === 0) {
    return collection;
  }
  const config = {
    keys: fields || Object.keys(collection[0]),
    threshold: params?.threshold || DEFAULT_SEARCH_THRESHOLD,
    distance: params?.distance || DEFAULT_SEARCH_DISTANCE,
    includeMatches: true,
  };
  const fuse = new Fuse(collection, config);
  return fuse.search(text).map((match) => match.item);
};

export const getFilteredList = ({ filters, filterStates, collection, params = {} }) => {
  let filteredData = [];
  const applyMainFilter = () => {
    const isActiveMain = hasActiveFilter(filters.main, filterStates);
    if (filters.main && isActiveMain) {
      getFilterItems(filters.main).forEach((item) => {
        if (filterStates[item.id]) {
          filteredData = filteredData.concat(getListByFilter({ field: item.field, value: item.value, collection }));
        }
      });
    } else {
      filteredData = filteredData.concat(collection);
    }
  };
  const applyIntersectionFilter = () => {
    if (filters.intersection) {
      getFilterItems(filters.intersection).forEach((item) => {
        const isVisible = typeof item.isVisible !== 'undefined' ? item.isVisible : true;
        if (filterStates[item.id] && isVisible) {
          filteredData = getListByFilter({ field: item.field, value: item.value, collection: filteredData });
        }
      });
    }
  };
  const applyTextFilter = () => {
    if (filters.text && filterStates[filters.text.id]) {
      const text = filterStates[filters.text.id];
      const exactMatch = filterText({ text, filters, collection: filteredData });
      if (params && exactMatch.length !== 1) {
        filteredData = searchFuzzyMatches({
          text,
          collection: filteredData,
          fields: filters.text.fields,
          params,
        });
      } else if (!params || exactMatch.length === 1) {
        filteredData = exactMatch;
      }
    }
  };

  applyMainFilter();
  applyIntersectionFilter();
  applyTextFilter();
  return filteredData;
};
